// App-version helpers for the apps the dashboard cares about.
//
// Two ideas live here:
//   1. The set of "tracked" apps — the player, updater and monitoring
//      service that we ship and version-check. The Apps tab uses this
//      list to label installed apps and decide whether they're outdated.
//   2. Version comparison and lookup — a simple dotted-int compare and
//      a couple of getters that work against the live RTDB shapes.
//
// Lifted out of DevicesPage so the new device-detail page can reuse the
// same logic without importing the page module.

// Our MDM agent ships under two identities, one per product line (2026-09-23). A device only ever
// carries ONE of each pair, and each identity has its own mdm/app_store node, so the dashboard has
// to recognise both instead of matching a single hard-coded package name.
//
//   platform line  our own firmware boxes, agent runs in the system uid
//   MDM line       retail OEM devices (Lenovo, Hisense, Philips) managed through Device Owner
export const MONITORING_PACKAGES = [
    'rs.maketv.monitoringservice', // platform line
    'rs.musicdj.mdm',              // MDM line
];

export const UPDATER_PACKAGES = [
    'rs.musicdj.updater',    // platform line
    'rs.musicdj.mdmupdater', // MDM line
];

export const PLAYER_PACKAGE = 'rs.musicdj.player'; // one identity on both lines

/** The agent itself - hiding its Close/Uninstall buttons keeps the control channel alive. */
export const isMonitoringPkg = (pkg) => MONITORING_PACKAGES.includes(pkg);
export const isUpdaterPkg = (pkg) => UPDATER_PACKAGES.includes(pkg);

export const LINE_PLATFORM = 'platform';
export const LINE_MDM = 'mdm';

/**
 * Which line a PACKAGE belongs to, or null when it belongs to neither and may go on any device
 * (the Player, and every third-party app).
 */
export function packageLine(pkg) {
    if (pkg === MONITORING_PACKAGES[0] || pkg === UPDATER_PACKAGES[0]) return LINE_PLATFORM;
    if (pkg === MONITORING_PACKAGES[1] || pkg === UPDATER_PACKAGES[1]) return LINE_MDM;
    return null;
}

/**
 * Which line a DEVICE is on, read from the agent it actually carries. null when the device has no
 * agent yet (freshly imaged, or its app_list has not been pushed), in which case we cannot tell -
 * and callers deliberately fall back to offering everything rather than hiding the very app the
 * operator needs in order to fix that device.
 */
export function deviceLine(device) {
    const mon = findDeviceApp(device, MONITORING_PACKAGES);
    if (mon) return packageLine(mon.packageName);
    const upd = findDeviceApp(device, UPDATER_PACKAGES);
    return upd ? packageLine(upd.packageName) : null;
}

/**
 * May this package be offered for a set of target devices?
 *
 * Installing the platform agent on a Lenovo (or the MDM agent on a firmware box) produces an APK
 * the device can never run: different signing key, and on Android 12 and older the sharedUserId
 * makes the install fail outright. Worse, it would be a second agent competing with the real one.
 * So an agent package is offered only when EVERY target device is known to be on that line.
 * Line-agnostic packages (Player, third-party) are always offered.
 *
 * @param {string} pkg package name from the app store
 * @param {Array<Object>} targetDevices the device snapshots the command would go to
 */
export function isPackageAllowedForDevices(pkg, targetDevices) {
    const line = packageLine(pkg);
    if (!line) return true;                       // not an agent - always fine
    const list = targetDevices || [];
    if (list.length === 0) return true;           // nothing selected yet - do not pre-filter
    return list.every((d) => deviceLine(d) === line);
}

/**
 * Packages a remote command must never uninstall - mirror of the device-side ProtectedPackages
 * guard. Both identities of both apps, so the list is correct whichever line the device is on.
 */
export const PROTECTED_PACKAGES = [
    ...MONITORING_PACKAGES,
    ...UPDATER_PACKAGES,
    'com.teamviewer.host.market', // remote access to the box
];
export const isProtectedPkg = (pkg) => PROTECTED_PACKAGES.includes(pkg);

export const TRACKED_APPS = [
    { packageName: PLAYER_PACKAGE,                displayName: 'Player'     },
    { packageName: 'rs.musicdj.updater',          displayName: 'Updater'    },
    { packageName: 'rs.musicdj.mdmupdater',       displayName: 'Updater'    },
    { packageName: 'rs.maketv.monitoringservice', displayName: 'Monitoring' },
    { packageName: 'rs.musicdj.mdm',              displayName: 'Monitoring' },
];

/**
 * Look up an app's installed version on a device.
 * The device reports either `versionName` (Android Play form) or
 * `appVersion` (legacy field) — accept whichever is present.
 *
 * @param {Object} device a `mdm/devices/<MAC>` snapshot
 * @param {string} packageName e.g. "rs.musicdj.player"
 * @returns {string|null}
 */
export function getDeviceAppVersion(device, packageName) {
    const list = device?.app_list || [];
    const app = list.find((a) => a?.packageName === packageName);
    return app?.versionName || app?.appVersion || null;
}

/**
 * Find whichever identity of a multi-identity app (monitoring, updater) this device actually has.
 * Returns the package name together with its installed version, so the caller can then look the
 * latest version up in THAT identity's app_store node - comparing a Lenovo against the platform
 * line's store entry would produce a bogus "needs update".
 *
 * @param {Object} device a `mdm/devices/<MAC>` snapshot
 * @param {string[]} packageNames e.g. MONITORING_PACKAGES
 * @returns {{packageName: string, version: string}|null} null when none of them is installed
 */
export function findDeviceApp(device, packageNames) {
    const list = device?.app_list || [];
    for (const packageName of packageNames) {
        const app = list.find((a) => a?.packageName === packageName);
        const version = app?.versionName || app?.appVersion;
        if (version) return { packageName, version };
    }
    return null;
}

/**
 * Latest known version for a package, read from the app_store at
 * `mdm/apps`. Keys there have dots replaced with underscores.
 *
 * @param {Object} apps the `mdm/apps` snapshot keyed by safeKey
 * @param {string} packageName
 * @returns {string|null}
 */
export function getLatestAppVersion(apps, packageName) {
    const safeKey = packageName.replace(/\./g, '_');
    return apps?.[safeKey]?.latestVersion || null;
}

/**
 * True iff the installed dotted-int version is strictly older than the
 * latest one. Returns false when either value is missing — we can't be
 * confident an update is needed without both halves of the comparison.
 *
 * @param {string|null|undefined} installed e.g. "3.0.135"
 * @param {string|null|undefined} latest    e.g. "3.0.136"
 */
export function needsAppUpdate(installed, latest) {
    if (!installed || !latest) return false;
    const i = installed.split('.').map(Number);
    const l = latest.split('.').map(Number);
    for (let k = 0; k < Math.max(i.length, l.length); k++) {
        const a = i[k] || 0;
        const b = l[k] || 0;
        if (a < b) return true;
        if (a > b) return false;
    }
    return false;
}
